import { api } from '@/services/api';

// ============= TYPES =============

export interface CostTotals {
  totalUSD: number;
  totalBRLDirect: number;
  totalBRLConverted: number;
  totalBRL: number;
  estimatedTaxBRL: number;
  totalWithTaxBRL: number;
  rate: number;
  byProvider: CostGroup[];
  byCategory: CostGroup[];
  byService: CostGroup[];
}

export interface CostGroup {
  _id: string;
  totalUSD: number;
  totalBRL: number;
  estimatedTaxBRL: number;
  totalWithTaxBRL: number;
}

export interface DashboardItem {
  _id: string;
  totalAmountUSD: number;
  totalAmountBRLDirect: number;
  totalBRL: number;
  estimatedTaxBRL: number;
  totalWithTaxBRL: number;
  count: number;
}

export interface TrendItem {
  _id: string;
  totalAmountUSD: number;
  totalAmountBRLDirect: number;
  totalBRL: number;
  estimatedTaxBRL: number;
  totalWithTaxBRL: number;
  byCategory: { category: string; amount: number; currency: string }[];
}

export interface CostItem {
  id_cost: string;
  provider: string;
  source_type: string;
  category: string;
  service_name: string;
  amountOriginal: number;
  currencyOriginal: string;
  amountBRL: number;
  estimatedTaxBRL: number;
  totalWithTaxBRL: number;
  period_start: string;
  recurring?: boolean;
  recurring_active?: boolean;
  recurring_original_id?: string | null;
}

export interface PaginatedCosts {
  data: CostItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ManualCostPayload {
  provider?: string;
  service_name: string;
  amount: number;
  period_start: string;
  period_end: string;
  description?: string;
  recurring?: boolean;
}

// ============= SERVICE =============

export const costsService = {
  getTotals: async (startDate: string, endDate: string): Promise<CostTotals> => {
    const response = await api.get(`/costs/totals?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  },

  getDashboard: async (startDate: string, endDate: string, groupBy: string = 'service'): Promise<{ data: DashboardItem[] }> => {
    const response = await api.get(`/costs/dashboard?startDate=${startDate}&endDate=${endDate}&groupBy=${groupBy}`);
    return response.data;
  },

  getTrend: async (startDate: string, endDate: string): Promise<{ data: TrendItem[] }> => {
    const response = await api.get(`/costs/trend?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  },

  /**
   * Gera dados de tendência a partir dos itens da listagem.
   * Usado como fallback quando a API /costs/trend não retorna dados.
   */
  buildTrendFromList: (items: CostItem[]): TrendItem[] => {
    const byDate = new Map<string, TrendItem>();

    for (const item of items) {
      const dateKey = item.period_start.split('T')[0];
      const existing = byDate.get(dateKey);

      if (existing) {
        existing.totalBRL += item.amountBRL;
        existing.estimatedTaxBRL += item.estimatedTaxBRL;
        existing.totalWithTaxBRL += item.totalWithTaxBRL;
      } else {
        byDate.set(dateKey, {
          _id: dateKey,
          totalAmountUSD: item.currencyOriginal === 'USD' ? item.amountOriginal : 0,
          totalAmountBRLDirect: item.currencyOriginal === 'BRL' ? item.amountOriginal : 0,
          totalBRL: item.amountBRL,
          estimatedTaxBRL: item.estimatedTaxBRL,
          totalWithTaxBRL: item.totalWithTaxBRL,
          byCategory: [{ category: item.category, amount: item.amountOriginal, currency: item.currencyOriginal }],
        });
      }
    }

    return Array.from(byDate.values()).sort((a, b) => a._id.localeCompare(b._id));
  },

  getList: async (startDate: string, endDate: string, page: number = 1, limit: number = 20): Promise<PaginatedCosts> => {
    const response = await api.get(`/costs/list?startDate=${startDate}&endDate=${endDate}&page=${page}&limit=${limit}`);
    return response.data;
  },

  /**
   * Busca apenas despesas manuais (source_type === 'manual').
   * Usado para exibir despesas manuais em seção separada no topo.
   */
  getManualItems: async (startDate: string, endDate: string): Promise<CostItem[]> => {
    const allItems: CostItem[] = [];
    let page = 1;
    const limit = 100;

    while (true) {
      try {
        const response = await api.get(`/costs/list?startDate=${startDate}&endDate=${endDate}&page=${page}&limit=${limit}`);
        const items = response.data?.data || [];
        const manualItems = items.filter((item: CostItem) => item.source_type === 'manual');
        allItems.push(...manualItems);

        if (items.length < limit) break;
        page++;
      } catch {
        break;
      }
    }

    return allItems;
  },

  /**
   * Busca TODOS os itens (todas as páginas) para uso no trend.
   * Usa limit alto para pegar tudo em poucas requisições.
   */
  getAllItems: async (startDate: string, endDate: string): Promise<CostItem[]> => {
    const allItems: CostItem[] = [];
    let page = 1;
    const limit = 100;

    while (true) {
      try {
        const response = await api.get(`/costs/list?startDate=${startDate}&endDate=${endDate}&page=${page}&limit=${limit}`);
        const items = response.data?.data || [];
        allItems.push(...items);

        if (items.length < limit) break; // Última página
        page++;
      } catch {
        break;
      }
    }

    return allItems;
  },

  createManual: async (payload: ManualCostPayload): Promise<any> => {
    const response = await api.post('/costs/manual', payload);
    return response.data;
  },

  syncAWS: async (startDate: string, endDate: string): Promise<any> => {
    const response = await api.post(`/costs/sync?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  },

  update: async (id: string, data: Partial<ManualCostPayload>): Promise<any> => {
    const response = await api.put(`/costs/${id}`, data);
    return response.data;
  },

  remove: async (id: string): Promise<any> => {
    const response = await api.delete(`/costs/${id}`);
    return response.data;
  },

  deactivateRecurring: async (id: string): Promise<any> => {
    const response = await api.patch(`/costs/${id}/recurring/deactivate`);
    return response.data;
  },
};

export default costsService;
